import _ from 'lodash';
import moment from 'moment';
import {
  openDatabase,
  addData,
  getData,
  deleteData,
  getAllData,
  updateData,
  readModifyWriteData,
  isRetryableWriteError,
  clearIndexedDBError,
} from './utils';

import { deleteDataFromDBBeat, getBeatDataKeys } from './dbBeatData';
import { deleteDataFromDBECG } from './dbECGData';
import { deleteDataFromDBState } from './dbState';
import { deleteDataFromDBHistoryState } from './dbHistoryState';
import { countLivingTabs, hasLivingTab } from './activeTabs';

// TODO(ts): download-progress fields (beatInfo/failedId/error) are shaped by
// Worker/beats.worker.ts and workerHandler.ts, not by a GraphQL document or schema.
export type StudyMetadataValue = {
  beatInfo?: any;
  createdAt?: number;
  lastModified?: number;
  message?: string;
  status?: string;
  failedId?: any[];
  error?: any;
  // Snapshot fields refreshed on every checkStudyMetadata: whether a tab has the study open, how
  // many tabs do, and the eviction rank (1 = evicted last / most protected; active first, then most
  // recent).
  active?: boolean;
  activeTabCount?: number;
  priority?: number;
  [key: string]: any;
};

export type StudyMetadataRecord = {
  key: string;
  value: StudyMetadataValue;
};

const NAME_DB_METADATA = 'DB_METADATA';
const NAME_OBJECT_STORE_METADATA = 'STUDY_METADATA';
const KEY_OBJECT_STORE_METADATA = 'key';
const STUDY_LIMIT = 3;

let metadataDB: IDBDatabase | null = null;
let metadataOpenInFlight: Promise<void> | null = null;

const METADATA_LOCK_NAME = 'DB_METADATA:eviction';

/**
 * Serialises a read→evict→seed critical section across browser tabs. Every tab shares the single
 * DB_METADATA database, and its eviction is a cross-tab read-modify-write, so concurrent tab opens
 * must not evict on stale snapshots. Uses the Web Locks API when available and otherwise runs the
 * section inline (single-tab, tests, older engines).
 */
const withMetadataLock = async <T>(fn: () => Promise<T>): Promise<T> => {
  const locks = (navigator as Navigator & { locks?: LockManager })?.locks;
  if (!locks?.request) return fn();
  return locks.request(METADATA_LOCK_NAME, fn) as Promise<T>;
};

/**
 * Opens the shared study metadata IndexedDB database and caches the handle in module scope. Coalesces concurrent opens through a single in-flight promise so no duplicate connection is created, and logs and swallows the error when the open fails.
 *
 * @returns A promise resolving once the metadata database handle has been established or the failure logged.
 */
const openDBMetadata = async () => {
  if (metadataDB) return;
  if (!metadataOpenInFlight) {
    metadataOpenInFlight = openDatabase(
      NAME_DB_METADATA,
      NAME_OBJECT_STORE_METADATA,
      KEY_OBJECT_STORE_METADATA,
    )
      .then((db) => {
        metadataDB = db;
      })
      .catch((error) => {
        console.error(`[${NAME_DB_METADATA}] Failed to open metadata database`, error);
      })
      .finally(() => {
        metadataOpenInFlight = null;
      });
  }
  await metadataOpenInFlight;
};

/**
 * Ensures the metadata database is open before an operation runs, opening it on demand when boot has not yet finished.
 *
 * @returns A promise resolving to the open metadata database handle (or null when the open failed).
 */
const ensureMetadataDb = async () => {
  if (!metadataDB) await openDBMetadata();
  return metadataDB!;
};

// Closes and reopens the metadata connection, replacing the cached singleton. Used to recover
// from a stale/closed handle before retrying a failed write.
const reopenMetadataDb = async () => {
  if (metadataDB) {
    try {
      metadataDB.close();
    } catch {
      /* connection already closing */
    }
    metadataDB = null;
  }
  metadataOpenInFlight = null;
  // Open directly rather than via openDBMetadata: that helper swallows open failures, which would
  // leave metadataDB null and make the retry run op(null). Here a failed reopen propagates.
  metadataDB = await openDatabase(
    NAME_DB_METADATA,
    NAME_OBJECT_STORE_METADATA,
    KEY_OBJECT_STORE_METADATA,
  );
  return metadataDB;
};

// DevTools' IndexedDB panel does not live-refresh. Instead of auto-logging, this reads the store on
// demand and prints it; it is registered on window as `logmeta()` so it can be called from
// the console whenever a fresh snapshot is wanted. Returns the records so the console shows them too.
const logMetadataSnapshot = async (): Promise<StudyMetadataRecord[]> => {
  try {
    const studies =
      (await getAllData<StudyMetadataRecord>(
        await ensureMetadataDb(),
        NAME_OBJECT_STORE_METADATA,
      )) ?? [];
    console.log(`[${NAME_DB_METADATA}] snapshot @ ${new Date().toISOString()} (${studies.length})`);
    const byPriority = [...studies].sort(
      (a, b) => (a.value?.priority ?? Infinity) - (b.value?.priority ?? Infinity),
    );
    console.table(
      byPriority.map((s) => ({
        key: s.key,
        priority: s.value?.priority,
        active: s.value?.active,
        activeTabCount: s.value?.activeTabCount,
        status: s.value?.beatInfo?.status,
        totalData: s.value?.beatInfo?.totalData,
        totalDownloaded: s.value?.beatInfo?.totalDownloaded,
        totalFailed: s.value?.beatInfo?.totalFailed,
        lastModified: s.value?.lastModified,
      })),
    );
    return studies;
  } catch (error) {
    console.error(`[${NAME_DB_METADATA}] snapshot log failed`, error);
    return [];
  }
};

if (typeof window !== 'undefined') {
  (window as unknown as { logmeta?: typeof logMetadataSnapshot }).logmeta = logMetadataSnapshot;
}

/**
 * Runs one metadata write, reopening the connection and retrying exactly once on a retryable
 * (transient or stale-handle) error. Opens the database on demand first; non-retryable errors
 * (quota, clone, constraint) propagate unchanged.
 *
 * @param op - Write built against the live metadata handle.
 * @returns A promise resolving to the operation's result.
 */
const runMetadataWrite = async <T>(op: (db: IDBDatabase) => Promise<T>): Promise<T> => {
  const db = await ensureMetadataDb();
  try {
    const result = await op(db);
    clearIndexedDBError();
    return result;
  } catch (error) {
    if (!isRetryableWriteError(error)) throw error;
    const result = await op(await reopenMetadataDb());
    clearIndexedDBError();
    return result;
  }
};

/**
 * Inserts a new study metadata record into the metadata database. Delegates to addData against the metadata object store.
 *
 * @param key - Study key the record is stored under.
 * @param value - Metadata payload describing the study's download progress.
 * @returns A promise resolving to the key produced by the insert.
 */
const addStudyMetadata = async (key: string, value: StudyMetadataValue) =>
  runMetadataWrite((db) => addData(db, NAME_OBJECT_STORE_METADATA, key, value));
/**
 * Reads one study's metadata record by its study key. Delegates straight to getData against the metadata object store.
 *
 * @param key - Study key the metadata record is stored under.
 * @returns A promise resolving to the metadata record for that key.
 */
const getStudyMetadata = async (key: string) =>
  getData<StudyMetadataRecord>(await ensureMetadataDb(), NAME_OBJECT_STORE_METADATA, key);
/**
 * Overwrites one study's metadata record wholesale. Delegates to updateData against the metadata object store.
 *
 * @param key - Study key of the record to overwrite.
 * @param value - Full metadata payload replacing the stored one.
 * @returns A promise resolving to the key produced by the put.
 */
const updateStudyMetadata = async (key: string, value: StudyMetadataValue) =>
  runMetadataWrite((db) => updateData(db, NAME_OBJECT_STORE_METADATA, key, value));
/**
 * Removes one study's metadata record from the metadata database. Delegates to deleteData against the metadata object store.
 *
 * @param key - Study key of the record to remove.
 * @returns A promise resolving to the deleted key.
 */
const deleteStudyMetadata = async (key: string) =>
  runMetadataWrite((db) => deleteData(db, NAME_OBJECT_STORE_METADATA, key));
/**
 * Reads every study metadata record held in the metadata database. Delegates straight to getAllData against the metadata object store.
 *
 * @returns A promise resolving to all stored study metadata records.
 */
const getAllStudyMetadata = async () =>
  getAllData<StudyMetadataRecord>(await ensureMetadataDb(), NAME_OBJECT_STORE_METADATA);

/**
 * Merges a partial metadata patch into the record already stored for one study. Reads the current record, strips null and function values from the patch, and writes the merged result back through a JSON round trip; warns when no record exists and logs any failure instead of throwing.
 *
 * @param key - Study key whose metadata is patched.
 * @param value - Partial metadata merged over the stored value.
 * @returns A promise resolving once the merged metadata has been written or the failure logged.
 */
const updateStudyMetadataV2 = async (key: string, value: StudyMetadataValue) => {
  try {
    let missing = false;
    // Read and merge in one transaction so concurrent ECG/beat progress writes cannot clobber each other.
    await runMetadataWrite((db) =>
      readModifyWriteData<StudyMetadataRecord>(
        db,
        NAME_OBJECT_STORE_METADATA,
        key,
        (currentData) => {
          if (!currentData) {
            missing = true;
            return undefined;
          }
          // Filter out null and non-serializable values
          const filteredValue = Object.fromEntries(
            Object.entries(value).filter(([_, v]) => v !== null && typeof v !== 'function'),
          );
          if (Object.keys(filteredValue).length === 0) {
            return undefined;
          }
          const updatedData = { ...currentData.value, ...filteredValue };
          return JSON.parse(JSON.stringify(updatedData));
        },
      ),
    );
    if (missing) {
      console.warn(`[${NAME_DB_METADATA}] No data found for the given key`);
    }
  } catch (error) {
    console.error(`[${NAME_DB_METADATA}] Failed to update study metadata`, error);
  }
};

/**
 * Stamps a metadata patch onto the study identified by the current URL and derives its download status. Takes the fourth path segment as the study key and aborts with an error log when it is absent, marks the status failed when the patch carries errors or failed ids, then writes the patch with a fresh lastModified timestamp.
 *
 * @param additionalMetadata - Metadata fields to merge, optionally carrying error and failedId.
 * @returns A promise resolving once the metadata update has been attempted.
 */
const updateStatusAndMetadata = async (additionalMetadata: StudyMetadataValue = {}) => {
  // Extract the key from the URL safely
  const pathSegments = window.location.pathname.split('/');
  const key = pathSegments.length > 2 ? pathSegments[3] : null;

  if (!key) {
    console.error(`[${NAME_DB_METADATA}] Invalid URL structure.`);
    return;
  }

  // Set default values for failedId and error
  const failedId = Array.isArray(additionalMetadata.failedId) ? additionalMetadata.failedId : [];
  const error = typeof additionalMetadata.error === 'object' ? additionalMetadata.error : {};

  // Determine lastModified and status
  const hasError = Object.keys(error).length > 0;
  if (hasError || failedId.length > 0) {
    additionalMetadata.status = 'failed';
  }

  // Call the update function with the new metadata
  try {
    const valueMetadata = { ...additionalMetadata, lastModified: Date.now() };
    await updateStudyMetadataV2(key, valueMetadata);
  } catch (error) {
    console.error(`[${NAME_DB_METADATA}] Failed to update metadata:`, error);
  }
};

/**
 * Rebuilds beatInfo from the ground truth so it always matches what IndexedDB actually holds. Reads the beat store's present keys, treats every ecgDataMap entry with a real beatFinalPath as expected, marks an entry downloaded when its hour-start key is present and failed otherwise, then atomically writes the recomputed totals, failed ids and status into the study's metadata record. errorCodes carry the run's transient HTTP codes (kept only while something still fails); startedAt overwrites the stored value when provided and is preserved otherwise. No-op when the URL key or the record is absent.
 *
 * @param ecgDataMap - The full study data map (never a re-download subset) whose entries define the expected beat files.
 * @param options - errorCodes seen this run, and an optional startedAt to stamp for a fresh full download.
 * @returns A promise resolving once beatInfo has been rewritten or skipped.
 */
type SyncBeatInfoOptions = { errorCodes?: any[]; startedAt?: number };

const syncBeatInfoImpl = async (
  key: string,
  ecgDataMap: any,
  { errorCodes = [], startedAt }: SyncBeatInfoOptions = {},
) => {
  const presentKeys = new Set(await getBeatDataKeys());
  const [downloadable, finalPathNull] = _.partition(
    ecgDataMap?.data ?? [],
    (item: any) => !_.isEmpty(item.beatFinalPath) && item.beatFinalPath !== 'null',
  );
  const idFinalPathNull = finalPathNull.map((item: any) => item.id);
  const failed = downloadable.filter((item: any) => !presentKeys.has(moment(item.start).valueOf()));
  const idDownloadFailed = failed.map((item: any) => item.id);
  const totalData = downloadable.length;
  const totalFailed = idDownloadFailed.length;
  const totalDownloaded = totalData - totalFailed;

  await runMetadataWrite((db) =>
    readModifyWriteData<StudyMetadataRecord>(db, NAME_OBJECT_STORE_METADATA, key, (current) => {
      // Metadata is the source of truth. When the record is missing, only self-heal it for a study
      // still open in a tab (its beat store exists, so its beatInfo must too); an evicted inactive
      // study stays evicted rather than being silently resurrected.
      if (!current && !hasLivingTab(key)) return undefined;
      const now = Date.now();
      const value = current ? { ...current.value } : { createdAt: now, message: '' };
      const beat = { ...(value.beatInfo || {}) };

      beat.status = totalFailed === 0 ? 'success' : 'failed';
      beat.totalData = totalData;
      beat.totalDownloaded = totalDownloaded;
      beat.totalFailed = totalFailed;
      beat.idDownloadFailed = idDownloadFailed;
      beat.idFinalPathNull = idFinalPathNull;
      beat.totalFinalPathNull = idFinalPathNull.length;
      beat.errorCodes = totalFailed
        ? [...new Set([...(beat.errorCodes || []), ...errorCodes])]
        : [];
      if (startedAt !== undefined) beat.startedAt = startedAt;
      beat.finishedAt = now;
      value.beatInfo = beat;

      value.lastModified = now;
      return JSON.parse(JSON.stringify(value));
    }),
  );
};

// Serialises syncBeatInfo per study: chained runs never interleave, and beat keys only grow between
// them (a re-download clear is gated), so the last run reads the fullest store and its write is
// authoritative — a partial-store run can no longer clobber a complete one.
const syncBeatInfoChains = new Map<string, Promise<unknown>>();

const syncBeatInfo = async (ecgDataMap: any, options: SyncBeatInfoOptions = {}) => {
  const pathSegments = window.location.pathname.split('/');
  const key = pathSegments.length > 2 ? pathSegments[3] : null;
  if (!key) return undefined;

  const run = (syncBeatInfoChains.get(key) ?? Promise.resolve())
    .catch(() => {})
    .then(() => syncBeatInfoImpl(key, ecgDataMap, options));
  syncBeatInfoChains.set(key, run);
  run.finally(() => {
    if (syncBeatInfoChains.get(key) === run) syncBeatInfoChains.delete(key);
  });
  return run;
};

/**
 * Tears down every per-study IndexedDB database belonging to one study. Deletes the beat, ECG, persisted state and undo history databases in sequence, logging any failure rather than propagating it.
 *
 * @param studyId - Study whose databases are deleted.
 * @returns A promise resolving once the study's databases have been deleted or the failure logged.
 */
const deleteOldestStudyDB = async (studyId: string) => {
  try {
    await deleteDataFromDBBeat(studyId);
    await deleteDataFromDBECG(studyId);
    await deleteDataFromDBState(studyId);
    await deleteDataFromDBHistoryState(studyId);
  } catch (error) {
    console.error(`[${NAME_DB_METADATA}] Delete oldest study database`, { error });
  }
};

/**
 * Purges IndexedDB databases left behind by studies no longer tracked in metadata. Lists all databases, deletes those whose 24 hex character id prefix is absent from the metadata keys while sparing the profile id currently in the URL, then re-lists and drops the remaining orphans through deleteOldestStudyDB unless localStorage still marks them as open.
 *
 * @returns A promise resolving once the orphaned databases have been deleted or the failure logged.
 */
const deleteUnusedDatabases = async () => {
  try {
    const idb =
      window.indexedDB || window.webkitIndexedDB || window.mozIndexedDB || window.msIndexedDB;
    if (!idb?.databases) return;

    const studies = await getAllStudyMetadata();
    const studyIds = new Set(studies.map((study) => study.key));
    const allDatabases = await idb.databases();

    // Per-study databases are named `${studyId}_${suffix}`; the study id is the segment before the
    // first '_'. hasLivingTab reads the same active-tabs-<id> registry the eviction paths use, so a
    // study open in another tab is spared in both directions below.
    const dbStudyIds = new Set<string>();
    allDatabases.forEach((db) => {
      const name = db?.name;
      if (!name || name === NAME_DB_METADATA || !name.includes('_DB_')) return;
      dbStudyIds.add(name.split('_')[0]);
    });

    // A per-study database is orphaned when no metadata record tracks it and no tab has it open.
    const orphanIds = [...dbStudyIds].filter((id) => !studyIds.has(id) && !hasLivingTab(id));
    // Metadata is the source of truth, but a record whose per-study databases are all gone and whose
    // study is not open in any tab is dead; drop it so metadata and IndexedDB cannot drift apart.
    const orphanMetaIds = [...studyIds].filter((id) => !dbStudyIds.has(id) && !hasLivingTab(id));

    await Promise.all([
      ...orphanIds.map((id) => deleteOldestStudyDB(id)),
      ...orphanMetaIds.map((id) => deleteStudyMetadata(id)),
    ]);
  } catch (error) {
    console.error(`[${NAME_DB_METADATA}] Error deleting databases:`, error);
  }
};

/**
 * Reports whether a study is currently open in any browser tab. Parses the per-study active-tabs list out of localStorage and treats a non-empty array as open, yielding false when the entry is missing or unparseable.
 *
 * @param studyId - Study whose open tabs are checked.
 * @returns True when at least one non-stale tab is registered as viewing the study.
 */
const isStudyOpen = (studyId: string) => hasLivingTab(studyId);

/**
 * Evicts least-recently-used studies once more than STUDY_LIMIT are stored, sparing any still open. Sorts studies oldest-first, always keeps every open study, fills the remaining keep-slots from the newest closed studies, then deletes the rest together with their per-study databases. No-op while at or below the limit.
 *
 * @param studies - All study metadata records currently stored (sorted in place).
 * @returns A promise resolving once the over-limit closed studies have been evicted.
 */
const evictStudiesOverLimit = async (studies: StudyMetadataRecord[]) => {
  if (studies.length <= STUDY_LIMIT) return;

  const sortedStudies = studies.sort(
    (a, b) => (a.value.lastModified ?? 0) - (b.value.lastModified ?? 0),
  );
  const openIndices = new Set<number>();
  sortedStudies.forEach((study, index) => {
    if (isStudyOpen(study.key)) openIndices.add(index);
  });

  const indicesToKeep = new Set(openIndices);
  let numNeeded = Math.max(0, STUDY_LIMIT - openIndices.size);
  for (let i = sortedStudies.length - 1; numNeeded > 0; i -= 1) {
    if (!openIndices.has(i)) {
      indicesToKeep.add(i);
      numNeeded -= 1;
    }
  }

  const studiesToDelete = sortedStudies.filter((_, index) => !indicesToKeep.has(index));

  await Promise.all(
    studiesToDelete.map((study) =>
      Promise.all([deleteStudyMetadata(study.key), deleteOldestStudyDB(study.key)]),
    ),
  );

  studiesToDelete.forEach((study) => {
    console.log(`[${NAME_DB_METADATA}] Deleted oldest study:`, study.key);
  });
};

/**
 * Frees storage by evicting the single least-recently-used closed study, sparing any study still
 * open in a tab. Used as a last resort when a per-study write hits the browser storage quota.
 *
 * @returns A promise resolving to true when a study was evicted, false when none could be freed.
 */
const evictOldestClosedStudy = async (): Promise<boolean> => {
  const studies = await getAllStudyMetadata();
  const oldestClosed = studies
    .filter((study) => !isStudyOpen(study.key))
    .sort((a, b) => (a.value.lastModified ?? 0) - (b.value.lastModified ?? 0))[0];

  if (!oldestClosed) return false;

  await Promise.all([deleteStudyMetadata(oldestClosed.key), deleteOldestStudyDB(oldestClosed.key)]);
  console.log(`[${NAME_DB_METADATA}] Evicted study to free storage:`, oldestClosed.key);
  return true;
};

/**
 * Builds the seed metadata record for a freshly tracked study, with empty beat download-progress counters stamped at the given time.
 *
 * @param currentTime - Epoch used for both createdAt and lastModified.
 * @returns The initial study metadata value.
 */
const createInitialStudyMetadata = (currentTime: number): StudyMetadataValue => ({
  beatInfo: {
    status: 'in_progress',
    totalDownloaded: 0,
    totalFailed: 0,
    totalData: 0,
    idDownloadFailed: [] as any[],
    errorCodes: [] as any[],
    startedAt: null as number | null,
    finishedAt: null as number | null,
  },
  createdAt: currentTime,
  lastModified: currentTime,
  message: '',
});

/**
 * Recomputes the eviction rank and open-state snapshot for every stored study and persists any that
 * changed. Studies are ordered active-first, then most-recently-modified, so priority 1 is the study
 * evicted last (most protected). Mirrors evictStudiesOverLimit's keep order, so the stored priority
 * reflects who would actually survive the next eviction.
 *
 * @returns A promise resolving once the refreshed priority/active fields have been written.
 */
const restampPriorityAndActive = async (touch?: {
  key: string;
  patch: Partial<StudyMetadataValue>;
}) => {
  // Best-effort snapshot refresh: callers (checkStudyMetadata, tab leave) do not await or catch it,
  // so it must never surface an unhandled rejection. `touch` folds a caller's field change (e.g. the
  // current study's lastModified) into the same write, avoiding a redundant second update.
  try {
    const studies = await getAllStudyMetadata();
    const countByKey = new Map(studies.map((study) => [study.key, countLivingTabs(study.key)]));
    const tabCount = (key: string) => countByKey.get(key) ?? 0;
    const ranked = [...studies].sort((a, b) => {
      const activeDiff = Number(tabCount(b.key) > 0) - Number(tabCount(a.key) > 0);
      return activeDiff !== 0
        ? activeDiff
        : (b.value.lastModified ?? 0) - (a.value.lastModified ?? 0);
    });

    await Promise.all(
      ranked.map((study, index) => {
        const extra = touch?.key === study.key ? touch.patch : undefined;
        const activeTabCount = tabCount(study.key);
        const active = activeTabCount > 0;
        const priority = index + 1;
        if (
          !extra &&
          study.value.active === active &&
          study.value.activeTabCount === activeTabCount &&
          study.value.priority === priority
        ) {
          return undefined;
        }
        return updateStudyMetadata(study.key, {
          ...study.value,
          ...extra,
          active,
          activeTabCount,
          priority,
        });
      }),
    );
  } catch (error) {
    console.error(`[${NAME_DB_METADATA}] Failed to restamp priority/active`, error);
  }
};

/**
 * Ensures a study has a metadata record and evicts the least recently used studies past the limit. Opens the metadata database when needed, evicts over-limit closed studies, refreshes every study's priority/active snapshot, then returns the existing record with a refreshed lastModified, or seeds a new record holding empty beatInfo progress counters.
 *
 * @param studyId - Study to look up or register; an empty value short-circuits the call.
 * @returns A promise resolving to the study's metadata value, or an empty object when no study id was given.
 */
const checkStudyMetadata = async (studyId: string) => {
  if (!metadataDB) {
    await openDBMetadata();
  }
  if (!studyId) return {};

  return withMetadataLock(async () => {
    const studies = await getAllStudyMetadata();
    const currentTime = Date.now();

    await evictStudiesOverLimit(studies);

    const existingStudy = studies.find((study) => study.key === studyId);
    let result: StudyMetadataValue;
    let touch: { key: string; patch: Partial<StudyMetadataValue> } | undefined;
    if (existingStudy) {
      if (existingStudy.value.beatInfo?.idDownloadFailed?.length > 0) {
        result = { ...existingStudy.value };
      } else {
        // Fold the lastModified refresh into the single restamp write below.
        result = { ...existingStudy.value, lastModified: Date.now() };
        touch = { key: studyId, patch: { lastModified: result.lastModified } };
      }
    } else {
      const newStudyMetadata = createInitialStudyMetadata(currentTime);
      await addStudyMetadata(studyId, newStudyMetadata);
      result = { ...newStudyMetadata };
    }

    await restampPriorityAndActive(touch);
    return result;
  });
};

/**
 * Runs the metadata maintenance pass without seeding a study: under the cross-tab lock, evicts
 * over-limit closed studies (deleting their per-study databases) and refreshes the priority/active
 * snapshot. Called when a tab leaves so a study that no longer meets the keep-priority is dropped
 * from IndexedDB immediately instead of waiting for the next report open. Best-effort — swallows
 * failures since callers do not await it.
 *
 * @returns A promise resolving once eviction and the snapshot refresh have been attempted.
 */
const reconcileMetadata = async () => {
  try {
    await withMetadataLock(async () => {
      const studies = await getAllStudyMetadata();
      await evictStudiesOverLimit(studies);
      await restampPriorityAndActive();
    });
  } catch (error) {
    console.error(`[${NAME_DB_METADATA}] Failed to reconcile metadata`, error);
  }
};

export {
  openDBMetadata,
  addStudyMetadata,
  getStudyMetadata,
  updateStudyMetadata,
  deleteStudyMetadata,
  getAllStudyMetadata,
  checkStudyMetadata,
  updateStatusAndMetadata,
  syncBeatInfo,
  deleteUnusedDatabases,
  evictOldestClosedStudy,
  restampPriorityAndActive,
  reconcileMetadata,
};
